/* eslint-disable react-hooks/exhaustive-deps */
import DocumentViewerModal from "components/DocumentViewer";
import { FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  fetchAppealRequestById,
  fetchDefendantAppealById,
} from "services/appealService";
import { FILE_URL } from "types";
import { DateView } from "utils/helper";

type Props = {
  prosecutorAppealId?: string | null;
  defendantAppealId?: string | null;
};

// Read-only view of the appeal a cassation originated from ("Previous Appeal").
const PreviousAppealView: FC<Props> = ({
  prosecutorAppealId,
  defendantAppealId,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<any>(null);
  const kind: "prosecutor" | "defendant" = defendantAppealId
    ? "defendant"
    : "prosecutor";

  useEffect(() => {
    setLoading(true);
    const run = defendantAppealId
      ? fetchDefendantAppealById(defendantAppealId)
      : prosecutorAppealId
        ? fetchAppealRequestById(prosecutorAppealId)
        : Promise.resolve(null);
    run
      .then((d) => setData(d))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [prosecutorAppealId, defendantAppealId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-red-600" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="rounded-xl border border-gray-200 bg-white p-8 text-center text-gray-500">
        {t("no_previous_appeal", { defaultValue: "No previous appeal found." })}
      </div>
    );
  }

  const rows = [
    {
      label: t("higher_court_type"),
      value: data?.higher_court_type,
    },
    {
      label: t("higher_court_level"),
      value: data?.higher_court_level,
    },
    {
      label: t("higher_court_hearing_date"),
      value: data?.higher_court_hearing_date
        ? DateView(data.higher_court_hearing_date)
        : "",
    },
    { label: t("crime_type"), value: data?.crime_type },
    {
      label: t("assigned_prosecutor"),
      value: data?.assigned_prosecutor,
    },
    {
      label: t("appeal_requested_date"),
      value: data?.created_at
        ? new Date(data.created_at).toLocaleDateString()
        : "",
    },
  ].filter((r) => r.value);

  const openFull = () => {
    if (kind === "defendant") {
      navigate(`/defendant-appeal/${data?.id}`);
    } else {
      navigate(`/prosecutor-appeal/${data?.id}`);
    }
  };

  const title =
    kind === "defendant"
      ? t("previous_defendant_appeal", { defaultValue: "Previous Appeal" })
      : t("previous_prosecutor_appeal", {
          defaultValue: "Previous Prosecutor Appeal",
        });

  return (
    <div className="space-y-4">
      {/* Header card */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="flex items-center justify-between gap-3 px-5 py-3 border-b border-gray-100 bg-gray-50/60">
          <div className="flex items-center gap-3">
            <div className="w-1.5 h-6 bg-red-600 rounded-full" />
            <h3 className="text-base font-semibold text-gray-900">{title}</h3>
          </div>
          <button
            type="button"
            onClick={openFull}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg border border-red-200 text-red-700 hover:bg-red-50 transition-colors"
          >
            ↗ {t("open_full_details", { defaultValue: "Open Full Details" })}
          </button>
        </div>
        <div className="p-5">
          <dl className="divide-y divide-gray-100">
            {rows.map((r, i) => (
              <div
                key={i}
                className="flex justify-between gap-4 py-2.5 text-sm"
              >
                <dt className="text-gray-500">{r.label}</dt>
                <dd className="font-medium text-gray-900 text-right">
                  {r.value}
                </dd>
              </div>
            ))}
          </dl>
        </div>
      </div>

      {/* Prosecutor response */}
      {data?.response_summary && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-5 py-3 border-b border-gray-100 bg-gray-50/60">
            <h3 className="text-base font-semibold text-gray-900">
              {t("prosecutor_response", { defaultValue: "Prosecutor Response" })}
            </h3>
          </div>
          <div className="p-5 space-y-4">
            <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-line">
              {data.response_summary}
            </p>
            {Array.isArray(data?.documents) && data.documents.length > 0 && (
              <DocumentViewerModal
                documents={
                  data.documents.filter(
                    (d: any) =>
                      d?.document_type === "defendant_appeal_response",
                  ) as any
                }
                baseUrl={`${process.env.REACT_APP_API_BASE_URL}${
                  kind === "defendant"
                    ? FILE_URL.DEFENDANT_APPEAL
                    : FILE_URL.APPEAL
                }`}
                layout="list"
                hideEmptyFile={true}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PreviousAppealView;
